import { patchOrganizationUser } from "@/api/endpoints/organization";
import {
  CURRENCIES,
  CURRENCY,
  Currency,
  CURRENCY_CODE_TO_LABEL,
  LOCALE,
  Locale,
  LOCALE_TO_LABEL,
  USER_ACCESS,
  UserAccess,
  USER_ACCESS_TO_LABEL,
} from "@/constants";
import { useCanManageUsers } from "@/hooks/useCanManageUsers";
import { User } from "@/types/User";
import { Spinner } from "@components/ui/spinner";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@components/ui/input-group";
import { zodResolver } from "@hookform/resolvers/zod";
import { i18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ClockIcon } from "lucide-react";
import { useEffect } from "react";
import { Controller, SubmitHandler, useFormState } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import { Switch } from "@components/ui/switch";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { toast } from "sonner";
import { z } from "zod";
import { Organization } from "@/types/Organization";

type UserFormProps = {
  user: User;
  timezones: string[];
  orgUuid: Organization["uuid"];
};

const userEditFormSchema = z.object({
  name: z.string(),
  email: z.email(),
  language: z.enum([LOCALE.SWEDISH, LOCALE.ENGLISH]),
  user_type: z.enum([
    USER_ACCESS.READONLY,
    USER_ACCESS.USER,
    USER_ACCESS.ADMIN,
  ]),
  currency: z.enum(CURRENCIES),
  time_zone: z.string(),
  send_mail_on_weekends: z.boolean(),
  preferred_mail_delivery_time: z.string(),
});

type UserEditForm = z.infer<typeof userEditFormSchema>;

type UserFormValues = z.infer<typeof userEditFormSchema>;

export default function UserForm({ user, timezones, orgUuid }: UserFormProps) {
  const { t } = useLingui();

  // Server state
  const queryClient = useQueryClient();
  const canManageUsers = useCanManageUsers();

  const { mutateAsync: mutateUpdateUser } = useMutation({
    mutationFn: (values: UserEditForm) =>
      patchOrganizationUser({
        organization_uuid: orgUuid,
        user_id: Number(user.id),
        user: values,
      }),
    onSuccess: (res) => {
      if (!res.ok) throw new Error();
      queryClient.invalidateQueries({ queryKey: [orgUuid, "users"] });
      toast.success(t`Användaren har uppdaterats`);
    },
    onError: () => {
      toast.error(t`Det gick inte att uppdatera användaren`);
    },
  });

  // Form state
  const form = useFormCompat<UserEditForm>({
    resolver: zodResolver(userEditFormSchema),
    defaultValues: {
      name: "",
      email: "",
      language: LOCALE.SWEDISH,
      user_type: USER_ACCESS.USER,
      currency: CURRENCY.SEK,
      time_zone: "",
      send_mail_on_weekends: false,
      preferred_mail_delivery_time: "",
    },
  });

  const formState = useFormState({ control: form.control });

  // Side-effects
  useEffect(() => {
    if (user) {
      form.reset({
        name: user.name,
        email: user.email,
        language: user.language,
        user_type: user.user_type,
        currency: user.currency,
        time_zone: user.time_zone,
        send_mail_on_weekends: user.send_mail_on_weekends,
        preferred_mail_delivery_time: user.preferred_mail_delivery_time,
      });
    }
  }, [user, form]);

  // Handlers
  const handleSubmit: SubmitHandler<UserFormValues> = (values) => {
    return mutateUpdateUser(values);
  };

  return (
    <form
      onSubmit={form.handleSubmit(handleSubmit)}
      className="flex flex-col gap-6 px-6"
    >
      <FieldGroup>
        <Controller
          name="name"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-name";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Namn</Trans>
                </FieldLabel>
                <Input
                  {...field}
                  id={id}
                  aria-invalid={fieldState.invalid}
                  disabled
                  readOnly
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="email"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-email";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>E-post</Trans>
                </FieldLabel>
                <Input
                  {...field}
                  id={id}
                  aria-invalid={fieldState.invalid}
                  disabled
                  readOnly
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="language"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-language";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Språk</Trans>
                </FieldLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!canManageUsers}
                >
                  <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                    <SelectValue>
                      {(locale: Locale | null) =>
                        locale ? i18n._(LOCALE_TO_LABEL[locale]) : t`Välj språk`
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="w-(--anchor-width)">
                    {Object.entries(LOCALE_TO_LABEL).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {i18n._(label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="user_type"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-user-type";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Åtkomstnivå</Trans>
                </FieldLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!canManageUsers}
                >
                  <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                    <SelectValue>
                      {(access: UserAccess | null) =>
                        access
                          ? i18n._(USER_ACCESS_TO_LABEL[access])
                          : t`Välj roll`
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="w-(--anchor-width)">
                    {Object.entries(USER_ACCESS_TO_LABEL).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {i18n._(label)}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="currency"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-currency";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Valuta</Trans>
                </FieldLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!canManageUsers}
                >
                  <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                    <SelectValue>
                      {(currency: Currency | null) =>
                        currency
                          ? `${currency}, ${i18n._(CURRENCY_CODE_TO_LABEL[currency])}`
                          : t`Välj valuta`
                      }
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="w-(--anchor-width)">
                    {Object.entries(CURRENCY_CODE_TO_LABEL).map(
                      ([value, label]) => (
                        <SelectItem key={value} value={value}>
                          {value}, {i18n._(label)}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="time_zone"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-timezone";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Tidszon</Trans>
                </FieldLabel>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={!canManageUsers}
                >
                  <SelectTrigger id={id} aria-invalid={fieldState.invalid}>
                    <SelectValue placeholder={t`Välj tidszon`} />
                  </SelectTrigger>
                  <SelectContent className="w-(--anchor-width)">
                    {timezones.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="preferred_mail_delivery_time"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-preferred-mail-delivery-time";
            return (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor={id}>
                  <Trans>Tidpunkt för mailrapporter</Trans>
                </FieldLabel>
                <InputGroup>
                  <InputGroupAddon>
                    <ClockIcon />
                  </InputGroupAddon>
                  <InputGroupInput {...field} type="time" id={id} step="1" />
                </InputGroup>
                <FieldDescription>
                  <Trans>Gäller mail på dag- och veckobasis</Trans>
                </FieldDescription>
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />

        <Controller
          name="send_mail_on_weekends"
          control={form.control}
          render={({ field, fieldState }) => {
            const id = "user-edit-weekend-emails";
            return (
              <Field
                orientation="horizontal"
                data-invalid={fieldState.invalid}
                className="items-start justify-between"
              >
                <div className="space-y-2">
                  <FieldLabel htmlFor={id}>
                    <Trans>Skicka e-post på helger</Trans>
                  </FieldLabel>
                  <FieldDescription>
                    <Trans>
                      Om den är avstängd pausar vi uppdateringar på lördag och
                      söndag.
                    </Trans>
                  </FieldDescription>
                </div>
                <Switch
                  id={id}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={!canManageUsers}
                />
                {fieldState.invalid && (
                  <FieldError errors={[fieldState.error]} />
                )}
              </Field>
            );
          }}
        />
      </FieldGroup>

      {canManageUsers && (
        <Button
          type="submit"
          className="self-end"
          disabled={formState.isSubmitting || !formState.isDirty}
        >
          <Trans>Spara</Trans>
          {formState.isSubmitting && <Spinner />}
        </Button>
      )}
    </form>
  );
}
